import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameWeek } from 'date-fns';

// Helper functions for consistent time calculation (same as AdminDataService)
const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const calculateDurationMinutes = (startTime, endTime, isCrossDate) => {
    if (!startTime || !endTime) return 0;
    
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    
    if (startMinutes === endMinutes) return 0;
    
    let durationMinutes;
    if (isCrossDate) {
        durationMinutes = (1440 - startMinutes) + endMinutes;
    } else {
        durationMinutes = endMinutes > startMinutes ? endMinutes - startMinutes : 0;
    }
    
    return durationMinutes;
};

const minutesToHours = (minutes) => {
    return minutes / 60;
};

// Calculate working hours for a single activity (same logic as AdminDataService)
const calculateActivityWorkingHours = (activity) => {
    const durationMinutes = calculateDurationMinutes(
        activity.startTime,
        activity.endTime,
        activity.isCrossDate || false
    );
    return minutesToHours(durationMinutes);
};

const TrendLineChart = ({ activitiesData, selectedMetric }) => {
    const containerRef = useRef(null);
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !activitiesData || activitiesData.length === 0 || !selectedMetric) return;

        d3.select(containerRef.current).selectAll("svg").remove();

        const container = containerRef.current;
        const { width, height } = container.getBoundingClientRect();
        const margin = { top: 8, right: 10, bottom: 30, left: 50 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        const svg = d3.select(container)
            .append("svg")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet")
            .style("width", "100%")
            .style("height", "100%");
            
        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const weeklyData = processDataByWeeks(activitiesData, selectedMetric);

        if (weeklyData.length === 0) {
            g.append("text")
                .attr("x", chartWidth / 2)
                .attr("y", chartHeight / 2)
                .attr("text-anchor", "middle")
                .style("font-size", "14px")
                .text("No data available for trend analysis");
            return;
        }

        const x = d3.scaleBand()
            .domain(weeklyData.map(d => d.weekLabel))
            .range([0, chartWidth])
            .padding(0.2);

        const maxValue = d3.max(weeklyData, d => d.value);
        // Ensure a reasonable minimum Y domain even for low values
        const yDomainMax = Math.max(maxValue * 1.2, selectedMetric === 'workingHours' ? 2 : 5);
        const y = d3.scaleLinear()
            .domain([0, yDomainMax])
            .nice()  
            .range([chartHeight, 0]);

        // Add grid lines
        g.append("g")
            .attr("class", "grid")
            .attr("opacity", 0.1)
            .call(d3.axisLeft(y)
                .tickSize(-chartWidth)
                .tickFormat("")
            );

        // Add X axis
        g.append("g")
            .attr("transform", `translate(0,${chartHeight})`)
            .call(d3.axisBottom(x))
            .selectAll("text")
            .attr("transform", "translate(-10,0)rotate(-45)")
            .style("text-anchor", "end")
            .style("font-size", "8px");

        // Add Y axis
        g.append("g")
            .call(d3.axisLeft(y).ticks(5).tickFormat(d => {
                // Format Y axis values based on the selected metric
                if (selectedMetric === 'workingHours') {
                    return d.toFixed(1) + 'h';
                } else {
                    return d;
                }
            }));

        // Add Y axis label
        g.append("text")
            .attr("text-anchor", "middle")
            .attr("transform", "rotate(-90)")
            .attr("y", -margin.left + 15)
            .attr("x", -chartHeight / 2)
            .style("font-size", "11px")
            .text(getYAxisLabel(selectedMetric));

        // Define line generator
        const line = d3.line()
            .x(d => x(d.weekLabel) + x.bandwidth() / 2)
            .y(d => y(d.value))
            .curve(d3.curveMonotoneX);
            
        // Define area generator
        const area = d3.area()
            .x(d => x(d.weekLabel) + x.bandwidth() / 2)
            .y0(chartHeight)
            .y1(d => y(d.value))
            .curve(d3.curveMonotoneX);

        // Add the area path with gradient
        const gradientId = `areaGradient-${selectedMetric}`;
        
        // Create gradient
        const gradient = svg.append("defs")
            .append("linearGradient")
            .attr("id", gradientId)
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "0%")
            .attr("y2", "100%");
            
        gradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", getLineColor(selectedMetric))
            .attr("stop-opacity", 0.8);
            
        gradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", getLineColor(selectedMetric))
            .attr("stop-opacity", 0.1);
        
        // Add area with gradient
        g.append("path")
            .datum(weeklyData)
            .attr("fill", `url(#${gradientId})`)
            .attr("d", area);

        // Add the line path
        g.append("path")
            .datum(weeklyData)
            .attr("fill", "none")
            .attr("stroke", getLineColor(selectedMetric))
            .attr("stroke-width", 2.5)
            .attr("d", line);

        // Add data points
        const circles = g.selectAll(".dot")
            .data(weeklyData)
            .enter()
            .append("circle")
            .attr("class", "dot")
            .attr("cx", d => x(d.weekLabel) + x.bandwidth() / 2)
            .attr("cy", d => y(d.value))
            .attr("r", 5)
            .attr("fill", "white")
            .attr("stroke", getLineColor(selectedMetric))
            .attr("stroke-width", 2)
            .style("cursor", "pointer");
            
        // Add pulsating animation to circles
        circles.append("animate")
            .attr("attributeName", "r")
            .attr("values", "5;6;5")
            .attr("dur", "3s")
            .attr("repeatCount", "indefinite");

        // Create tooltip container
        const tooltip = d3.select(container)
            .append("div")
            .attr("class", "tooltip")
            .style("opacity", 0)
            .style("position", "absolute")
            .style("background-color", "rgba(0, 0, 0, 0.8)")
            .style("color", "white")
            .style("padding", "5px 10px")
            .style("border-radius", "4px")
            .style("font-size", "12px")
            .style("pointer-events", "none")
            .style("z-index", 1000);
            
        // Add hover interaction to data points
        circles
            .on("mouseover", function(event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("r", 7)
                    .attr("stroke-width", 3);
                    
                // Set tooltip content first
                tooltip.html(getTooltipContent(d, selectedMetric));
                
                // Get precise positioning relative to the container
                const containerRect = container.getBoundingClientRect();
                const circleX = x(d.weekLabel) + x.bandwidth() / 2 + margin.left;
                const circleY = y(d.value) + margin.top;
                
                // Get tooltip dimensions after content is set
                tooltip.style("opacity", 0).style("display", "block");
                const tooltipNode = tooltip.node();
                const tooltipWidth = tooltipNode.offsetWidth;
                const tooltipHeight = tooltipNode.offsetHeight;
                
                // Calculate optimal position with smart boundary detection
                const padding = 10; // Distance from cursor/circle
                const containerWidth = containerRect.width;
                const containerHeight = containerRect.height;
                
                // Default position (right and below the data point)
                let xPosition = circleX + padding;
                let yPosition = circleY - tooltipHeight - padding;
                
                // Smart horizontal positioning
                if (xPosition + tooltipWidth > containerWidth - padding) {
                    // Move to left of the data point
                    xPosition = circleX - tooltipWidth - padding;
                }
                
                // If still overflows left, center it horizontally
                if (xPosition < padding) {
                    xPosition = Math.max(padding, (containerWidth - tooltipWidth) / 2);
                }
                
                // Smart vertical positioning
                if (yPosition < padding) {
                    // Move below the data point
                    yPosition = circleY + padding;
                }
                
                // If overflows bottom, move above
                if (yPosition + tooltipHeight > containerHeight - padding) {
                    yPosition = circleY - tooltipHeight - padding;
                }
                
                // Final boundary check - ensure tooltip stays within container
                xPosition = Math.max(padding, Math.min(xPosition, containerWidth - tooltipWidth - padding));
                yPosition = Math.max(padding, Math.min(yPosition, containerHeight - tooltipHeight - padding));
                
                // Apply position and show tooltip
                tooltip
                    .style("left", xPosition + "px")
                    .style("top", yPosition + "px")
                    .transition()
                    .duration(150)
                    .style("opacity", 1);
            })
            .on("mouseout", function() {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("r", 5)
                    .attr("stroke-width", 2);
                    
                tooltip
                    .transition()
                    .duration(150)
                    .style("opacity", 0)
                    .on("end", function() {
                        d3.select(this).style("display", "none");
                    });
            });

    }, [activitiesData, selectedMetric]);

    const getTooltipContent = (data, metric) => {
        const weekRange = `Week of ${data.weekLabel}`;
        let metricValue = '';
        
        switch(metric) {
            case 'activities':
                metricValue = `Activities: ${data.value}`;
                break;
            case 'workingHours':
                metricValue = `Hours: ${data.value.toFixed(2)}`;
                break;
            case 'artifacts':
                metricValue = `Artifacts: ${data.value}`;
                break;
            default:
                metricValue = `Value: ${data.value}`;
        }
        
        return `<div>${weekRange}</div><div>${metricValue}</div>`;
    };

    const processDataByWeeks = (activities, metric) => {
        if (!activities || activities.length === 0) return [];

        const dates = activities
            .filter(activity => activity.date)
            .map(activity => parseISO(activity.date));

        if (dates.length === 0) return [];

        const earliestDate = d3.min(dates);
        const latestDate = d3.max(dates);

        const firstWeekStart = startOfWeek(earliestDate, { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(latestDate, { weekStartsOn: 1 });

        const weeksInterval = eachDayOfInterval({ 
            start: firstWeekStart, 
            end: lastWeekEnd 
        }).filter(date => date.getDay() === 1);

        const weeklyData = weeksInterval.map(weekStart => {
            const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
            
            const activitiesInWeek = activities.filter(activity => {
                if (!activity.date) return false;
                const activityDate = parseISO(activity.date);
                return isSameWeek(activityDate, weekStart, { weekStartsOn: 1 });
            });

            const weekLabel = `${format(weekStart, 'MM/dd')}`;

            let value = 0;
            if (metric === 'activities') {
                value = activitiesInWeek.length;
            } else if (metric === 'workingHours') {
                value = activitiesInWeek.reduce((total, activity) => {
                    // Use the same calculation logic as AdminDataService for consistency
                    const durationHours = calculateActivityWorkingHours(activity);
                    return total + durationHours;
                }, 0);
            } else if (metric === 'artifacts') {
                value = activitiesInWeek.reduce((total, activity) => {
                    return total + (activity.files ? activity.files.length : 0);
                }, 0);
            }

            return {
                weekStart,
                weekEnd,
                weekLabel,
                value
            };
        });

        return weeklyData;
    };

    const getChartTitle = (metric) => {
        switch (metric) {
            case 'activities':
                return 'Weekly Activities Trend';
            case 'workingHours':
                return 'Weekly Working Hours Trend';
            case 'artifacts':
                return 'Weekly Artifacts Trend';
            default:
                return 'Weekly Trend';
        }
    };

    const getYAxisLabel = (metric) => {
        switch (metric) {
            case 'activities':
                return 'Activities Count';
            case 'workingHours':
                return 'Hours';
            case 'artifacts':
                return 'Artifacts Count';
            default:
                return 'Value';
        }
    };

    const getLineColor = (metric) => {
        switch (metric) {
            case 'activities':
                return '#4d94ff'; // Blue
            case 'workingHours':
                return '#4caf50'; // Green
            case 'artifacts':
                return '#ff9800'; // Orange
            default:
                return '#666666';
        }
    };

    return (
        <ChartWrapper>
            <TitleContainer>
                <Title>
                    {getChartTitle(selectedMetric)}
                    <InfoIconContainer 
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showInfo && (
                            <InfoTooltip>
                                <InfoTitle>Trend Chart Information</InfoTitle>
                                <InfoItem>
                                    <strong>Weekly Tracking:</strong> Shows how your team's {selectedMetric === 'activities' ? 'activity count' : selectedMetric === 'workingHours' ? 'working hours' : 'artifact count'} changes over time
                                </InfoItem>
                                <InfoItem>
                                    <strong>Data Points:</strong> Each circle represents one week's total
                                </InfoItem>
                                <InfoItem>
                                    <strong>Trend Line:</strong> Blue/Green/Orange line shows the overall pattern
                                </InfoItem>
                                <InfoItem>
                                    <strong>Gradient Fill:</strong> Visual representation of data distribution
                                </InfoItem>
                                <InfoItem>
                                    <strong>Interaction:</strong> Hover over data points for detailed information
                                </InfoItem>
                            </InfoTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <ChartContainer ref={containerRef} />
        </ChartWrapper>
    );
};

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    & svg {
        max-width: 100%;
        max-height: 100%;
    }
`;

const ChartWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0px;
    position: relative;
    flex-shrink: 0;
    height: 30px;
`;

const Title = styled.h3`
    font-size: 13px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #4285f4;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 12px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const InfoTooltip = styled.div`
    position: absolute;
    top: 20px;
    left: -280px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 300px;
    z-index: 1000;
`;

const InfoTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 6px;
`;

const InfoItem = styled.div`
    font-size: 12px;
    margin: 6px 0;
    line-height: 1.4;
    color: #555;
`;

export default React.memo(TrendLineChart); 